import { Fragment, ReactNode } from 'react'

import { i18n } from '@lingui/core'

import { CHAIN_INFO } from '@cowprotocol/common-const'
import { styled, useFeatureFlags } from '@cowprotocol/common-hooks'
import {
  getEtherscanLink,
  getExplorerAddressLink,
  getExplorerLabel,
  isInjectedWidget,
  shortenAddress,
} from '@cowprotocol/common-utils'
import { Command } from '@cowprotocol/types'
import { Badge, BadgeTypes, ExternalLink } from '@cowprotocol/ui'
import {
  ConnectionType,
  getIsInjectedMobileBrowser,
  useConnectionType,
  useDisconnectWallet,
  useIsWalletConnect,
  useWalletDetails,
  useWalletInfo,
} from '@cowprotocol/wallet'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useInjectedWidgetParams } from 'entities/injectedWidget'
import { Bell } from 'react-feather'

import Copy from 'legacy/components/Copy'
import { groupActivitiesByDay, useMultipleActivityDescriptors } from 'legacy/hooks/useRecentActivity'
import { useAppDispatch } from 'legacy/state/hooks'
import { updateSelectedWallet } from 'legacy/state/user/reducer'

import { useHasNotificationSubscription, useOpenNotificationSidebar } from 'modules/notifications'

import { useIsProviderNetworkUnsupported } from 'common/hooks/useIsProviderNetworkUnsupported'
import { UnsupportedNetworksText } from 'common/pure/UnsupportedNetworksText'

import { AccountIcon } from './AccountIcon'
import { ActivitiesList } from './ActivitiesList'
import {
  AccountControl,
  AccountGroupingRow,
  ActivityHeader,
  AddressLink,
  CreationDateRow,
  GetNotifiedButton,
  InfoCard,
  LowerSection,
  NetworkCard,
  NoActivityMessage,
  UnsupportedWalletBox,
  WalletAction,
  WalletActions,
  WalletName,
  WalletNameAddress,
  WalletSecondaryActions,
  WalletSelector,
  WalletWrapper,
  Wrapper,
} from './styled'
import { SurplusCard } from './SurplusCard'

import { useCloseAccountModal } from '../../hooks/useToggleAccountModal'
import { CowShedInfo } from '../CowShedInfo'
import { CreationDateText } from '../Transaction/styled'

const CowShedInfoStyled = styled(CowShedInfo)`
  margin-top: 10px;
`

const DATE_FORMAT_OPTION: Intl.DateTimeFormatOptions = {
  dateStyle: 'long',
}

export interface AccountDetailsProps {
  pendingTransactions: string[]
  confirmedTransactions: string[]
  ENSName?: string
  handleCloseOrdersPanel: Command
}

// TODO: Break down this large function into smaller functions
// TODO: Add proper return type annotation
// TODO: Reduce function complexity by extracting logic
// eslint-disable-next-line max-lines-per-function, complexity
export function AccountDetails({
  pendingTransactions = [],
  confirmedTransactions = [],
  ENSName,
  handleCloseOrdersPanel,
}: AccountDetailsProps): ReactNode {
  const { account, chainId } = useWalletInfo()
  const connectionType = useConnectionType()
  const walletDetails = useWalletDetails()
  const dispatch = useAppDispatch()
  const disconnectWallet = useDisconnectWallet()
  const isChainIdUnsupported = useIsProviderNetworkUnsupported()
  const closeAccountModal = useCloseAccountModal()
  const { standaloneMode } = useInjectedWidgetParams()
  const { areTelegramNotificationsEnabled } = useFeatureFlags()
  const { hasSubscription, isLoading: isNotificationSubscriptionLoading } = useHasNotificationSubscription()
  const openNotificationSidebar = useOpenNotificationSidebar()

  const handleGetNotifiedClick = (): void => {
    closeAccountModal()
    openNotificationSidebar()
  }

  const showGetNotifiedRow =
    areTelegramNotificationsEnabled && !isNotificationSubscriptionLoading && !hasSubscription && !isInjectedWidget()

  const explorerOrdersLink = account && getExplorerAddressLink(chainId, account)
  const explorerLabel = account ? getExplorerLabel(chainId, 'address', account) : undefined

  const activities = useMultipleActivityDescriptors({ chainId, ids: pendingTransactions.concat(confirmedTransactions) })
  const activitiesGroupedByDate = groupActivitiesByDay(activities)
  const activityTotalCount = activities?.length || 0

  const isWalletConnect = useIsWalletConnect()
  const isInjectedMobileBrowser = getIsInjectedMobileBrowser()

  // In case the wallet is connected via WalletConnect and has wallet name set, add the suffix to be clear
  // This to avoid confusion for instance when using Metamask mobile
  // When name is not set, it defaults to WalletConnect already
  const walletConnectSuffix = isWalletConnect && walletDetails?.walletName ? ` ` + t`(via WalletConnect)` : ''

  const handleDisconnectClick = async (): Promise<void> => {
    await disconnectWallet()
    handleCloseOrdersPanel()
    dispatch(updateSelectedWallet({ wallet: undefined }))
  }

  const networkLabel = CHAIN_INFO[chainId].label

  return (
    <Wrapper>
      <InfoCard>
        <AccountGroupingRow id="web3-account-identifier-row">
          <AccountControl>
            <WalletWrapper>
              <WalletSelector>
                <AccountIcon size={24} account={account} />

                {(ENSName || account) && (
                  <WalletNameAddress>{ENSName ? ENSName : account && shortenAddress(account)}</WalletNameAddress>
                )}
              </WalletSelector>

              {(ENSName || account) && <Copy toCopy={ENSName ? ENSName : account ? account : ''} />}
            </WalletWrapper>

            <WalletActions>
              {' '}
              {!isChainIdUnsupported && <NetworkCard title={networkLabel}>{networkLabel}</NetworkCard>}{' '}
              <WalletName>
                <Trans>Connected with</Trans> {walletDetails?.walletName} {walletConnectSuffix}
              </WalletName>
            </WalletActions>

            <CowShedInfoStyled onClick={closeAccountModal} />
          </AccountControl>
        </AccountGroupingRow>
        <AccountGroupingRow>
          <AccountControl>
            <WalletSecondaryActions>
              {!isInjectedMobileBrowser && account && !isChainIdUnsupported && (
                <AddressLink
                  hasENS={!!ENSName}
                  isENS={!!ENSName}
                  href={getEtherscanLink(chainId, 'address', ENSName ? ENSName : account)}
                >
                  {explorerLabel} ↗
                </AddressLink>
              )}

              {standaloneMode !== false && connectionType !== ConnectionType.GNOSIS_SAFE && (
                <WalletAction onClick={handleDisconnectClick}>
                  <Trans>Disconnect</Trans>
                </WalletAction>
              )}
            </WalletSecondaryActions>
          </AccountControl>
        </AccountGroupingRow>
      </InfoCard>

      {isChainIdUnsupported ? (
        <UnsupportedWalletBox>
          <UnsupportedNetworksText />
        </UnsupportedWalletBox>
      ) : (
        <>
          <SurplusCard />

          {activityTotalCount ? (
            <LowerSection id="account-activities-list">
              <ActivityHeader>
                <h5>
                  <Trans>Recent Activity</Trans> <span>{`(${activityTotalCount})`}</span>
                </h5>
                {showGetNotifiedRow && (
                  <GetNotifiedButton onClick={handleGetNotifiedClick}>
                    <Bell size={16} />
                    <Trans>Get trade alerts</Trans>
                    <Badge type={BadgeTypes.ALERT2}>
                      <Trans>New</Trans>
                    </Badge>
                  </GetNotifiedButton>
                )}
                {explorerOrdersLink && (
                  <ExternalLink href={explorerOrdersLink}>
                    <Trans>View all orders</Trans> ↗
                  </ExternalLink>
                )}
              </ActivityHeader>

              <div>
                {activitiesGroupedByDate.map(({ date, activities }) => (
                  <Fragment key={date.getTime()}>
                    {/* TODO: style me! */}
                    <CreationDateRow>
                      <CreationDateText>{date.toLocaleString(i18n.locale, DATE_FORMAT_OPTION)}</CreationDateText>
                    </CreationDateRow>
                    <ActivitiesList activities={activities} />
                  </Fragment>
                ))}
                {explorerOrdersLink && (
                  <ExternalLink href={explorerOrdersLink}>
                    <Trans>View all orders</Trans> ↗
                  </ExternalLink>
                )}
              </div>
            </LowerSection>
          ) : (
            <LowerSection id="account-activities-list">
              <NoActivityMessage>
                <span>
                  <Trans>Your activity will appear here...</Trans>
                </span>
              </NoActivityMessage>
            </LowerSection>
          )}
        </>
      )}
    </Wrapper>
  )
}
